import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { Table, TableVariant } from '@column/column-ui-kit';

import { CardTransactionRow, cardTransactionColumns } from '../Transactions/shared';
import { ROUTE } from '~/app/routes';
import { EmptyState, Loading, Section } from '~/components';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardTransactionRepository } from '~/repositories/CardTransactionRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';

import { CardProps } from './Card';

interface Params {
  id: string;
}

export const PageCardTransactions: React.FC = () => {
  const navigate = useNavigate();
  const { id: cardId } = useParams<keyof Params>() as Params;
  useOutletContext<CardProps>(); // Ensures we are nested in card details.
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [cardId, currentPlatform?.id, isSandbox, resetPagination]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['card-transactions', 'by-card', cardId, currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () =>
      CardTransactionRepository.listCardTransactions({
        cardId,
        limit: pageSize,
        startingAfter: cursor,
      }),
    enabled: Boolean(cardId && currentPlatform?.id),
    placeholderData: keepPreviousData,
  });

  const transactions = data?.cardTransactions ?? [];
  const hasMore = Boolean(data?.hasMore);

  return (
    <Inner p={0}>
      <Section headline="Transactions">
        <Loading isLoading={isLoading}>
          <Inner px="var(--page-margin)" py={0}>
            {isError ? (
              <EmptyState headline="Error" text="Failed to load card transactions." />
            ) : transactions.length === 0 && !hasPrevious ? (
              <EmptyState headline="No transactions yet" text="Transactions will appear here for this card." />
            ) : (
              <Table<CardTransactionRow>
                columns={cardTransactionColumns}
                data={transactions}
                variant={TableVariant.List}
                pagination={{
                  hasNext: hasMore,
                  hasPrevious,
                  pageIndex,
                  pageSize,
                  onNext: () => {
                    if (transactions.length > 0) {
                      onNext({ cursor: transactions[transactions.length - 1].id });
                    }
                  },
                  onPrevious,
                  onPageSizeChange: setPageSize,
                }}
                onRowClick={(row) => {
                  navigate(`${ROUTE.CARD_TRANSACTIONS}/edit/${row.id}`);
                }}
              />
            )}
          </Inner>
        </Loading>
      </Section>
    </Inner>
  );
};
